import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import Hls from "hls.js";
import {
  failoverDelayMs,
  findHealthyBackup,
  recordHlsError,
  resetHlsErrors,
} from "../lib/hlsFailover";

const API_BASE =
  process.env.REACT_APP_BACKEND_URL ||
  `${window.location.protocol}//${window.location.hostname}:8001`;

function apiUrl(path) {
  return `${API_BASE}${path.startsWith("/") ? path : `/${path}`}`;
}

function proxyStreamUrl(url) {
  if (!url) return "";
  return apiUrl(`/api/public/streaming/proxy/hls?url=${encodeURIComponent(url)}`);
}

function transcodePreviewUrl(eventId, accessToken = "") {
  const suffix = accessToken ? `?access_token=${encodeURIComponent(accessToken)}` : "";
  return apiUrl(`/api/public/streaming/transcode/${eventId}/index.m3u8${suffix}`);
}


function pickEvent(payload) {
  return payload?.event || payload?.data || payload || {};
}

function getSources(event) {
  const player = event.player || {};
  return Array.isArray(player.sources) ? player.sources : [];
}

function statusLabel(event) {
  const status = String(event.status || "").toLowerCase();

  if (status.includes("live")) return "Live";
  if (status.includes("termin")) return "Terminato";
  if (status.includes("end")) return "Terminato";
  if (status.includes("programm")) return "Programmato";
  if (status.includes("scheduled")) return "Programmato";

  return event.status || "Programmato";
}

export default function WatchPage() {
  const { id } = useParams();
  const videoRef = useRef(null);
  const hlsRef = useRef(null);
  const failoverErrorsRef = useRef(resetHlsErrors());
  const failoverInProgressRef = useRef(false);
  const failoverExhaustedRef = useRef(false);
  const failoverTimerRef = useRef(null);
  const failedBackupIndexesRef = useRef(new Set());
  const accessStorageKey = `calciohub_live_access_${id}`;

  const [event, setEvent] = useState(null);
  const [activeSourceIndex, setActiveSourceIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [playerError, setPlayerError] = useState("");
  const [accessToken] = useState(() => {
    const urlToken = new URLSearchParams(window.location.search).get("access_token") || "";
    if (urlToken) {
      localStorage.setItem(accessStorageKey, urlToken);
      return urlToken;
    }
    return localStorage.getItem(accessStorageKey) || "";
  });

  useEffect(() => {
    let mounted = true;

    async function loadEvent() {
      setLoading(true);
      setPlayerError("");

      try {
        const query = accessToken ? `?access_token=${encodeURIComponent(accessToken)}` : "";
        const response = await fetch(apiUrl(`/api/public/streaming/events/${id}${query}`), {
          headers: { Accept: "application/json" },
        });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        const payload = await response.json();
        const loadedEvent = pickEvent(payload);

        if (mounted) {
          setEvent(loadedEvent);
          setActiveSourceIndex(0);
          failoverErrorsRef.current = resetHlsErrors();
          failoverInProgressRef.current = false;
          failoverExhaustedRef.current = false;
          if (failoverTimerRef.current) clearTimeout(failoverTimerRef.current);
          failoverTimerRef.current = null;
          failedBackupIndexesRef.current = new Set();
        }
      } catch (error) {
        console.error("[Streaming] event load failed", error);
        if (mounted) setPlayerError("Streaming non disponibile");
      } finally {
        if (mounted) setLoading(false);
      }
    }

    loadEvent();

    return () => {
      mounted = false;
    };
  }, [id, accessToken]);

  const sources = useMemo(() => getSources(event || {}), [event]);
  const activeSource = sources[activeSourceIndex];

  useEffect(() => {
    const video = videoRef.current;
    let disposed = false;

    if (!video || !activeSource?.url) {
      return undefined;
    }

    setPlayerError("");

    if (hlsRef.current) {
      hlsRef.current.destroy();
      hlsRef.current = null;
    }

    video.pause();
    video.removeAttribute("src");
    video.load();

    const originalUrl = activeSource.url;
    const useHls = activeSource.delivery_mode === "direct_hls";
    const playbackUrl = useHls ? proxyStreamUrl(originalUrl) : transcodePreviewUrl(id, accessToken);

    console.info("[Streaming] original source", originalUrl);
    console.info("[Streaming] playback source", playbackUrl);
    console.info("[Streaming] mode", useHls ? "hls" : "ffmpeg-hls-preview");

    if (Hls.isSupported()) {
      const hls = new Hls({
        enableWorker: true,
        lowLatencyMode: true,
        backBufferLength: 90,
      });

      hlsRef.current = hls;

      async function attemptFailover() {
        if (
          failoverInProgressRef.current ||
          failoverExhaustedRef.current ||
          activeSourceIndex !== 0 ||
          activeSource.delivery_mode !== "direct_hls"
        ) return;
        failoverInProgressRef.current = true;

        try {
          const nextIndex = await findHealthyBackup(
            sources,
            failedBackupIndexesRef.current,
            async (index) => {
              const query = accessToken ? `?access_token=${encodeURIComponent(accessToken)}` : "";
              const response = await fetch(apiUrl(`/api/public/streaming/events/${id}/failover/verify${query}`), {
                method: "POST",
                headers: { "Content-Type": "application/json", Accept: "application/json" },
                body: JSON.stringify({ source_index: index }),
              });
              return response.ok ? response.json() : { healthy: false };
            }
          );
          if (disposed) return;
          if (nextIndex !== null) {
            hls.destroy();
            if (hlsRef.current === hls) hlsRef.current = null;
            setActiveSourceIndex(nextIndex);
            return;
          }
          failoverExhaustedRef.current = true;
          setPlayerError("Streaming non disponibile");
        } finally {
          failoverInProgressRef.current = false;
        }
      }

      hls.on(Hls.Events.ERROR, (_, data) => {
        console.error("[Streaming] HLS error", data);

        failoverErrorsRef.current = recordHlsError(failoverErrorsRef.current, data);
        const delay = failoverDelayMs(failoverErrorsRef.current);
        if (delay !== null && !failoverTimerRef.current) {
          failoverTimerRef.current = setTimeout(() => {
            failoverTimerRef.current = null;
            void attemptFailover();
          }, delay);
        }

        if (!data?.fatal) return;

        if (data.type === Hls.ErrorTypes.NETWORK_ERROR) {
          hls.startLoad();
          return;
        }

        if (data.type === Hls.ErrorTypes.MEDIA_ERROR) {
          hls.recoverMediaError();
          return;
        }

        if (data.type !== Hls.ErrorTypes.NETWORK_ERROR && data.type !== Hls.ErrorTypes.MEDIA_ERROR) {
          setPlayerError("Streaming non disponibile");
        }
      });

      const resetFailoverThreshold = () => {
        failoverErrorsRef.current = resetHlsErrors();
        if (failoverTimerRef.current) clearTimeout(failoverTimerRef.current);
        failoverTimerRef.current = null;
      };

      hls.on(Hls.Events.MANIFEST_LOADED, resetFailoverThreshold);
      hls.on(Hls.Events.LEVEL_LOADED, resetFailoverThreshold);
      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        console.info("[Streaming] HLS manifest parsed");
        video.play().catch((error) => {
          console.warn("[Streaming] autoplay blocked", error);
        });
      });

      hls.loadSource(playbackUrl);
      hls.attachMedia(video);
    } else if (video.canPlayType("application/vnd.apple.mpegurl")) {
      video.src = playbackUrl;
      video.addEventListener(
        "loadedmetadata",
        () => {
          video.play().catch((error) => {
            console.warn("[Streaming] autoplay blocked", error);
          });
        },
        { once: true }
      );
    } else {
      setPlayerError("Il browser non supporta questo tipo di streaming");
    }

    return () => {
      disposed = true;
      if (failoverTimerRef.current) clearTimeout(failoverTimerRef.current);
      failoverTimerRef.current = null;
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }

      video.pause();
      video.removeAttribute("src");
      video.load();
    };
  }, [activeSource?.url, activeSource?.delivery_mode, activeSourceIndex, sources, id, accessToken]);

  function goFullscreen() {
    const video = videoRef.current;
    if (video?.requestFullscreen) video.requestFullscreen();
  }

  if (loading) {
    return (
      <main className="min-h-screen bg-slate-950 text-white flex items-center justify-center">
        <div className="text-slate-300">Caricamento streaming...</div>
      </main>
    );
  }

  if (!event) {
    return (
      <main className="min-h-screen bg-slate-950 text-white flex items-center justify-center">
        <div className="text-red-300">Evento non trovato</div>
      </main>
    );
  }

  if (event.access_required) {
    return (
      <main className="min-h-screen bg-slate-950 text-white">
        <section className="mx-auto flex min-h-screen max-w-3xl flex-col justify-center px-4 py-8">
          <div className="rounded-md border border-slate-800 bg-slate-900 p-5 shadow-xl">
            <div className="text-sm text-slate-400">
              {event.competition || event.sport || "Streaming"}
            </div>

            <h1 className="mt-2 text-2xl font-bold md:text-4xl">
              {event.title || `${event.home_team || ""} - ${event.away_team || ""}`.trim() || "Live CalcioHub"}
            </h1>

            <div className="mt-5 rounded-md border border-blue-900/60 bg-blue-950/30 p-4 text-sm text-blue-100">
              L'accesso a questa live viene verificato esclusivamente dentro Telegram.
              Apri il link della live dal Telegram Access Bot: dopo la verifica riceverai il pulsante
              per guardare la diretta.
            </div>
          </div>
        </section>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-slate-950 text-white">
      <section className="mx-auto max-w-5xl px-4 py-6">
        <div className="mb-5">
          <div className="text-sm text-slate-400">
            {event.competition || event.sport || "Streaming"}
          </div>

          <h1 className="mt-1 text-2xl font-bold md:text-4xl">
            {event.title || `${event.home_team || ""} - ${event.away_team || ""}`.trim()}
          </h1>

          <div className="mt-2 flex flex-wrap gap-3 text-sm text-slate-300">
            {event.home_team && event.away_team && (
              <span>
                {event.home_team} vs {event.away_team}
              </span>
            )}

            {(event.date || event.time) && (
              <span>
                {event.date || ""} {event.time || ""}
              </span>
            )}

            <span>{statusLabel(event)}</span>
          </div>
        </div>

        {event.image_url || event.image ? (
          <img
            src={event.image_url || event.image}
            alt={event.title || "Streaming"}
            className="mb-5 h-56 w-full rounded-md object-cover md:h-80"
          />
        ) : null}

        <div className="overflow-hidden rounded-md border border-slate-800 bg-black">
          <video
            ref={videoRef}
            className="aspect-video w-full bg-black"
            controls
            playsInline
            muted={false}
          />

          {playerError && (
            <div className="border-t border-slate-800 bg-slate-950 px-4 py-3 text-sm text-red-300">
              {playerError}
            </div>
          )}
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={goFullscreen}
            className="rounded-md bg-white px-4 py-2 text-sm font-semibold text-slate-950"
          >
            Fullscreen
          </button>

          {sources.length > 1 &&
            sources.map((source, index) => (
              <button
                key={`${source.label}-${index}`}
                type="button"
                onClick={() => setActiveSourceIndex(index)}
                className={
                  index === activeSourceIndex
                    ? "rounded-md bg-emerald-500 px-4 py-2 text-sm font-semibold text-slate-950"
                    : "rounded-md border border-slate-700 px-4 py-2 text-sm text-slate-200"
                }
              >
                {source.label}
              </button>
            ))}
        </div>

        {event.description && (
          <p className="mt-5 max-w-3xl text-sm leading-6 text-slate-300">
            {event.description}
          </p>
        )}
      </section>
    </main>
  );
}
